import React from 'react';
import { castArray, get, find } from 'lodash';
import { withRouter } from 'react-router-dom';
import searchQuery from 'query-string';

// React view component to render
import { memberPrescriptionDetailSummary as MemberPrescriptionDetailSummaryView } from 'ui/views/widgets/memberPrescriptionDetailSummary';
import { MemberPrescriptionDetailSummaryService } from 'services/memberPrescriptionDetailSummary';
import { SERVICE_RESPONSE_STATUS, ORIGIN, GLOBAL_LABELS, ERROR_RESPONSE_TEMPLATE, SIZES } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import { addRxToCheckoutState } from 'resolvers/checkoutState';
import * as successOverlay from 'utils/successOverlay';
import { getLabel } from 'utils/getLabel';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';

// import * as notificationOverlay from 'utils/notificationOverlay';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberPrescriptionDetailSummary } from 'ui/containers/MemberPrescriptionDetailSummary';
 *
 * // render
 * <MemberPrescriptionDetailSummary propName='propValue'/>
 */
class _MemberPrescriptionDetailSummary extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
        };

        // bind functions
        this.handleActionClick = this.handleActionClick.bind( this );
        this.initiateChangeNextRefillDate = this.initiateChangeNextRefillDate.bind( this );
        this.submitChangeNextRefillDate = this.submitChangeNextRefillDate.bind( this );

        // member prescriptions instance
        this.memberPrescriptionDetailSummaryService = new MemberPrescriptionDetailSummaryService();

        // start member detail data fetch
        this.fetchComponentData();

    }

    /**
     * @desc: Action executed  when MSR clicks on the Cart icon
     */
    async initiateCheckoutProcess( { url, payload } ) {
        log.debug( 'MemberDetailsOrdersEntityWidget.initiateCheckoutProcess()' );

        // cancel ongoing request
        this.memberPrescriptionDetailSummaryService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // fetch component data
        const { status } = await this.memberPrescriptionDetailSummaryService.initiateCheckoutProcess( {
            url: url,
            data: {
                context: {
                    memberId: payload.memberId,
                },
                data: {
                    rxIds: payload.rxIds,
                },
            },
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {

                // redirect to edit order checkout page
                this.props.history.push( {
                    pathname: DASHBOARD_SUB_ROUTES.NEW_ORDER,
                    search: searchQuery.stringify( {
                        origin: ORIGIN.MEMBER_MANAMGENT
                    } ),
                    state: { memberId: payload.memberId }
                } );
            } );
        } else {
            errorOverlay.open(); // show error
        }
    }

    // fetch component data
    async fetchComponentData() {
        log.debug( 'MemberPrescriptionDetailSummary.fetchComponentData()' );
        
         // cancel ongoing request
         this.memberPrescriptionDetailSummaryService.cancel();

        try {
            const { status, data }  = await this.memberPrescriptionDetailSummaryService.fetchComponentData( {
                url: this.props.widget.url,
                data: this.props.requestPayload,
            } );

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );
            } else {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    componentData: data,
                } );
            }

        } catch( e ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
            } );
        }
    }

    // handle actions
    handleActionClick( { type, value, item = {} } ) {
        log.debug( 'MemberPrescriptionDetailSummary.handleActionClick()' );

        // get member id and rx id
        const memberId = get( this.props, 'requestPayload.context.memberId', null );
        const rxId = get( this.props, 'requestPayload.context.rxId', null );

        if ( 'cell' === type && 'nextRefillDate' === item.name ) {
            const fieldAction = find( this.state.componentData.fieldActions, { name: item.name }, {} );

            this.initiateChangeNextRefillDate( {
                fieldAction,
                payload: {
                    memberId,
                    rxId
                }
            } );
        }

        if( 'headAction' === type && 'toDos' === value.name ) {

            return this.fetchToDoWidgetInfo( {
                action: value,
                payload: {
                    memberId,
                    rxId
                }
            } );
        }

        if( 'headAction' === type && 'refill' === value.name ) {

            // update the store with the rxId
            addRxToCheckoutState( { memberId, rxId: castArray( rxId ) } );

            successOverlay.open( {
                message: getLabel( value.onSuccess ),
            } );
        }

        if ( 'headAction' === type && 'enableAuto' === value.name ) {

            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.ENABLE_AUTO_REFILL,
                search: searchQuery.stringify( {
                    rxId,
                    origin: ORIGIN.RX_DETAIL
                } ),
                state: { memberId: memberId }
            } );
        }

        if ( 'headAction' === type && 'disableAuto' === value.name ) {
            return this.initiateDisableAuto( {
                url: value.url,
                payload: {
                    data: {
                        memberId,
                        rxId
                    }
                },
            } );
        }
    }

    /**
     * @desc Shows an overlay to change next refill date
     */
     initiateChangeNextRefillDate( { fieldAction, payload } ) {
        log.debug( 'MemberPrescriptionDetailSummary.initiateChangeNextRefillDate()' );

        overlay.open( {
            settings: {
                modalType: OVERLAY_VIEWS.CHANGE_NEXT_REFILL_DATE,
                showCloseButton: false,
                ignoreOutsideClick: false,
            },
            config: {},
            data: {
                ...fieldAction,
                values: { nextRefillDate: get( this.state.componentData, 'rxData.values.nextRefillDate' ) },
                payload,
                onFormSubmit: this.submitChangeNextRefillDate,
                onCancelClick: () => {
                    overlay.close();
                },
            }
        } );
    }

    /**
     * @desc Submits next refill date change request
     */
     async submitChangeNextRefillDate( { actions, payload, formData } ) {
        log.debug( 'MemberPrescriptionDetailSummary.submitChangeNextRefillDate()' );

        // cancel ongoing request
        this.memberPrescriptionDetailSummaryService.cancel();

        // close overlay
        overlay.close();

        // show page level preloader
        pageLevelPreloader.show();

        // find `save` action
        const saveAction = find( actions, { name: 'save' }, {} );

        // submit request and get response status
        const { status, data } = await this.memberPrescriptionDetailSummaryService.submitChangeNextRefillDate( {
            url: saveAction.url,
            data: {
                context: {
                    memberId: payload.memberId
                },
                data: {
                    rxId: payload.rxId,
                    ...formData
                }
            }
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: true,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
            }, () => {  // fetch new data
                this.fetchComponentData();
            } );
        } else {
            const template = get( data, 'template', ERROR_RESPONSE_TEMPLATE.DEFAULT );

            // if template's available in error response
            if( template === ERROR_RESPONSE_TEMPLATE.notification ) {

                // show template error overlay
                errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
            } else {

                // show default error overlay
                errorOverlay.open();
            }
        }
    }

    /**
     * @desc: Action executed  when MSR clicks on the Disable Auto icon
     */
    async initiateDisableAuto( { url, payload } ) {
        log.debug( 'MemberDetailsPrescriptionsEntityWidget.initiateDisableAuto()' );

        // cancel ongoing request
        this.memberPrescriptionDetailSummaryService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberPrescriptionDetailSummaryService.disableAutoData( {
            url: url,
            context: {
                memberId: payload.data.memberId,
            },
            data: payload
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.DISABLE_AUTO_REFILL,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...data,
                    routeChange: false,
                    onSumbitClick: async () => {

                        overlay.close();

                        // find action with `name: submit`
                        const action = find( get( data, 'actions', [] ), { name: 'submit' } );


                        // cancel ongoing request
                        this.memberPrescriptionDetailSummaryService.cancel();

                        // show page level preloader
                        pageLevelPreloader.show();

                        // get status and service data
                        const { _status, _data } = await this.memberPrescriptionDetailSummaryService.enableAuto( {
                            url: action.url,
                            data: {
                                context: {
                                    memberId: payload.data.memberId,
                                },
                                data: {
                                    rxId: payload.data.rxId,
                                    enableAuto: false,
                                },
                            },
                        } );

                        // show page level preloader
                        pageLevelPreloader.hide();

                        // check if response status was success
                        if( SERVICE_RESPONSE_STATUS.SUCCESS === _status ) {

                            successOverlay.open( {
                                message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                            } );

                            this.setState( {
                                isFetching: true
                            }, () => {

                                // display success overlay
                                this.fetchComponentData();
                                
                            } );
                        } else {

                            const template = get( _data, 'template', ERROR_RESPONSE_TEMPLATE.DEFAULT );

                            if( template === ERROR_RESPONSE_TEMPLATE.notification ) {
                                errorOverlay.open( { message: _data.description, size: SIZES.SMALL } );
                            } else {
                                errorOverlay.open();
                            }

                            // errorOverlay.open(); // show error
                        }

                    },
                    onCancelClick: () => {
                        overlay.close();
                    },
                },
            } );
        } else {
            errorOverlay.open(); // show error
        }
    }

    /**
     * @desc: Action executed  when MSR clicks on the To-Dos icon
     */
    async fetchToDoWidgetInfo( { action, payload } ) {
        log.debug( 'MemberPrescriptionDetailsSummary.fetchToDoWidgetInfo()' );

        pageLevelPreloader.show();

        // cancel ongoing request
        this.memberPrescriptionDetailSummaryService.cancel();

        try {
            const { status, data } = await this.memberPrescriptionDetailSummaryService.fetchToDoWidgetInfo( {
                url: action.url,
                data: {
                    context: {
                        memberId: payload.memberId,
                        prescriptionId: payload.rxId
                    }
                }
            } );

            pageLevelPreloader.hide();

            // check if response status was success
            if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {

                errorOverlay.open();

            } else {

                // open overlay and send the `data`
                overlay.open( {
                    settings: {
                        modalType: OVERLAY_VIEWS.TO_DO_PRESCRIPTION,
                        showCloseButton: false,
                        ignoreOutsideClick: false,
                    },
                    data: {
                        payload,
                        ...action,
                        ...data,
                        onCancelClick: () => {
                            overlay.close();
                        },
                    }
                } );
            }

        } catch ( e ) {

            pageLevelPreloader.hide();

            errorOverlay.open();
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.memberPrescriptionDetailSummaryService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberPrescriptionDetailSummaryView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                componentData = { this.state.componentData }
                requestPayload={ this.props.requestPayload }
                onActionClick={ this.handleActionClick }
            />
        );
    }
}

// set display name
_MemberPrescriptionDetailSummary.displayName = 'MemberPrescriptionDetailSummary';

// set default props
_MemberPrescriptionDetailSummary.defaultProps = {};

// prop types
_MemberPrescriptionDetailSummary.propTypes = {};

export const MemberPrescriptionDetailSummary = withRouter( _MemberPrescriptionDetailSummary );
